import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import theme from "../../theme";
import { PAGE_SIZE, formatCurrency } from "./utils";

const useStyles = makeStyles(() => ({
  container: {
    background: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "15px",
    overflow: "hidden",
  },
  headerCell: {
    color: theme.colors.DodgerBlue,
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    borderBottom: "1px solid #F6FAFF",
    whiteSpace: "nowrap",
  },
  columnCell: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    borderBottom: "1px solid #F6FAFF",
    verticalAlign: "middle",
  },
  row: {
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: "#ECF3FF" },
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-400px 0" },
    "100%": { backgroundPosition: "400px 0" },
  },
  skeleton: {
    height: "14px",
    borderRadius: "7px",
    background: `linear-gradient(90deg, ${theme.colors.Zumthor} 25%, #f4f8ff 37%, ${theme.colors.Zumthor} 63%)`,
    backgroundSize: "800px 100%",
    animation: "$shimmer 1.4s ease infinite",
  },
  emptyWrap: {
    padding: "60px 24px",
    textAlign: "center",
  },
  emptyTitle: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "18px",
    marginBottom: "8px",
  },
  emptyHint: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "14px",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    flexWrap: "wrap",
    gap: "10px",
  },
  countText: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    opacity: 0.8,
  },
}));

export function Skeleton({ width = "100%" }) {
  const classes = useStyles();
  return <div className={classes.skeleton} style={{ width }} />;
}

// Generic table with skeleton-loading rows and an empty state.
// columns: [{ key, header, render(row), align, width }]
export function DataTable({
  columns,
  data,
  loading,
  emptyTitle = "Nothing here yet",
  emptyHint = "",
  skeletonRows = 6,
  onRowClick,
}) {
  const classes = useStyles();
  const rows = Array.isArray(data) ? data : [];
  const showEmpty = !loading && rows.length === 0;

  return (
    <TableContainer className={classes.container}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                width={col.width || "auto"}
                className={classes.headerCell}
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: skeletonRows }).map((_, rIdx) => (
                <TableRow key={`sk-${rIdx}`}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={classes.columnCell}>
                      <Skeleton width={col.skeletonWidth || "70%"} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row, rIdx) => (
                <TableRow
                  key={row.id || rIdx}
                  className={classes.row}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      className={classes.columnCell}
                      onClick={
                        col.stopPropagation
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {showEmpty ? (
        <div className={classes.emptyWrap}>
          <div className={classes.emptyTitle}>{emptyTitle}</div>
          {emptyHint ? <div className={classes.emptyHint}>{emptyHint}</div> : null}
        </div>
      ) : null}
    </TableContainer>
  );
}

// Pagination bar driven off the list envelope's count / next / previous.
export function PaginationBar({
  count = 0,
  page = 1,
  next,
  previous,
  pageSize = PAGE_SIZE,
  onChange,
}) {
  const classes = useStyles();
  const totalPages = Math.max(1, Math.ceil(Number(count || 0) / pageSize));
  const hasNext = next !== undefined ? !!next : page < totalPages;
  const hasPrev = previous !== undefined ? !!previous : page > 1;

  if (!count) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, count);

  return (
    <div className={classes.paginationBar}>
      <span className={classes.countText}>
        Showing {from}–{to} of {count}
      </span>
      <Pagination
        count={totalPages}
        page={page}
        showFirstButton
        showLastButton
        // Guard navigation against the envelope's next/previous flags.
        onChange={(_, value) => {
          if (value === page) return;
          if (value > page && !hasNext) return;
          if (value < page && !hasPrev) return;
          onChange && onChange(value);
        }}
      />
    </div>
  );
}

// Budget usage bar: total_redeemed / budget_limit. "Uncapped" when no limit.
export function BudgetBar({ totalRedeemed, budgetLimit }) {
  if (budgetLimit === null || budgetLimit === undefined) {
    return (
      <span
        style={{
          color: theme.colors.DodgerBlue,
          fontFamily: theme.fontFamily.MARKPROBOOK,
          fontSize: "13px",
        }}
      >
        Uncapped
      </span>
    );
  }

  const limit = Number(budgetLimit) || 0;
  const used = Number(totalRedeemed) || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const nearFull = pct >= 90;

  return (
    <div style={{ minWidth: "140px" }}>
      <div
        style={{
          height: "8px",
          borderRadius: "999px",
          backgroundColor: theme.colors.Zumthor,
          overflow: "hidden",
        }}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: "999px",
            backgroundColor: nearFull
              ? theme.colors.RejectRed
              : theme.colors.DodgerBlue,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div
        style={{
          marginTop: "5px",
          fontSize: "11.5px",
          fontFamily: theme.fontFamily.MARKPROBOOK,
          color: theme.colors.Violet,
          opacity: 0.85,
        }}
      >
        {formatCurrency(used)} / {formatCurrency(limit)} ({pct}%)
      </div>
    </div>
  );
}
